import { Alert, Box, Divider, Grid, MenuItem, Paper, Typography } from "@mui/material";
import React, { useState } from "react";
import { ToastContent } from "../../../../shared/components";
import LoadingDialog from "../../../../shared/components/LoadingModalDialog/LoadingModalDialog";
import { VTextField } from "../../../../shared/forms";
import { VMaskTextField } from "../../../../shared/forms/VMaskTextField";
import { useToast } from "../../../../shared/hooks";
import { PeopleService } from "../../../../shared/services/api/people/PeopleService";
import { globalColorTytle, globalMaskDocument } from "../../../../shared/utils";
import { AutoCompleteClientCapitalization } from "../AutoCompleteClientCapitalization";

interface ISectionProps {
    data?: any;
    formRef: any;
}

export const CapitalizationInitialDataSection: React.FC<ISectionProps> = ({ data, formRef }) => {
    const [isLoadingDocument, setLoadingDocument] = useState(false);
    const [lastOwnerDocument, setLastOwnerDocument] = useState("");
    const toastHandler = useToast();

    const showToastError = (message: string) => {
        toastHandler.present({
            type: "error",
            position: "top-right",
            messageOrContent: <ToastContent title="Erro" content={message} />,
        });
    };

    const handleCPF = (value: string) => {
        const documento = value.replace(/\D/g, "").trim();

        if (documento === lastOwnerDocument) return;
        if (documento.length < 11) return; // CPF incompleto

        setLastOwnerDocument(documento);
        setLoadingDocument(true);

        PeopleService.getPersonDataByDocument(documento)
            .then((data) => {
                if (!data) {
                    showToastError(`Documento ${globalMaskDocument(value)} não localizado. Verifique se está correto.`);
                    setLoadingDocument(false);
                } else {
                    const { name, email, phone } = data.naturalPersonData || {};
                    formRef.current?.setFieldValue("ownerName", name || "");

                    setLoadingDocument(false);
                }
            })
            .catch(() => {
                showToastError(`Erro na pesquisa do documento ${globalMaskDocument(value)}`);
                setLoadingDocument(false);
            });
    };

    return (
        <>
            <LoadingDialog isLoading={isLoadingDocument} loadingText={`Localizando documento...`} />
            <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">
                <Grid container direction="column" padding={2} spacing={2}>
                    <Grid item xs={11}>
                        <Typography variant="h6" color={globalColorTytle}>
                            Dados Iniciais
                        </Typography>
                    </Grid>

                    <Grid container item direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="text.primary" gutterBottom>
                                Informações gerais
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <AutoCompleteClientCapitalization
                                isExternalLoading={false}
                                namecampo="realEstateId"
                                labelSelect="Imobiliária"
                                nameDefault={data?.clientName}
                                onChange={(id) => formRef.current?.setFieldValue("realEstateId", id)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <VTextField fullWidth label="Seguradora emissora*" name="insuranceCompany" select>
                                <MenuItem value="Icatu Seguros S/A">Icatu Seguros S/A</MenuItem>
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <VTextField fullWidth label="Status" name="status" select>
                                <MenuItem value="issuing">Em emissão</MenuItem>
                                <MenuItem value="issued">Título emitido</MenuItem>
                                <MenuItem value="awaiting_payment">Aguardando pagamento</MenuItem>
                                <MenuItem value="pending">Com pendências</MenuItem>
                                <MenuItem value="canceled">Cancelado</MenuItem>
                                <MenuItem value="redemption_requested">Resgate solicitado</MenuItem>
                                <MenuItem value="redemption_completed">Resgate concluído</MenuItem>
                            </VTextField>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    <Grid container item direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="text.primary" gutterBottom>
                                Informações do proprietário
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField
                                fullWidth
                                label="Documento*"
                                name="ownerDocument"
                                mask="cpf"
                                onBlur={(e) => handleCPF(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <VTextField fullWidth label="Nome*" name="ownerName" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Email*" name="ownerEmail" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <VMaskTextField fullWidth label="Telefone*" name="ownerPhone" mask="cellphone" />
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>

                    <Grid container item direction="row" spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" color="text.primary" gutterBottom>
                                Valores da locação e título
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor do aluguel*" name="rentAmount" mask="real" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor do condomínio" name="condominiumAmount" mask="real" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor da água" name="waterAmount" mask="real" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor da energia" name="energyAmount" mask="real" />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor do gás" name="gasAmount" mask="real" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor do IPTU" name="iptuAmount" mask="real" />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Início da vigência" name="startDate" type="date" InputLabelProps={{ shrink: true }} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Fim da vigência" name="endDate" type="date" InputLabelProps={{ shrink: true }} />
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Plano*" name="plan" select>
                                <MenuItem value="12">12 meses</MenuItem>
                                <MenuItem value="18">18 meses</MenuItem>
                                <MenuItem value="24">24 meses</MenuItem>
                                <MenuItem value="30">30 meses</MenuItem>
                                <MenuItem value="36">36 meses</MenuItem>
                            </VTextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VTextField fullWidth label="Multiplicador*" name="multiplier" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <VMaskTextField fullWidth label="Valor do título*" name="titleAmount" mask="real" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                Valor sugerido: {data?.titleAmount}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <VTextField fullWidth label="Número do título" name="titleNumber" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <VTextField fullWidth label="Número da proposta" name="proposalNumber" />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <VTextField fullWidth label="Data de emissão" name="issueDate" type="date" InputLabelProps={{ shrink: true }} />
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Alert severity={'warning'}>Atenção! O resgate integral só é feito no aniversário conforme plano selecionado (ao final da vigência do título).</Alert>
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};
